/**
 * What `SafeMessageBody` writes as its frame's height after each measurement of the email's document
 * (F20-c WI-1.3 plan Decision 11): the frame follows its content, with a limit on growth.
 *
 * An email whose content height depends on the frame's own height (an element with `min-height:
 * 100vh`, or an `html, body { height: 100% }` the guard style cannot override) grows the frame on
 * every measurement. So consecutive growth steps are counted, and after `MAX_FRAME_GROWTH_STEPS` the
 * component stops observing and keeps the last height; the frame then scrolls inside. A measurement
 * that does not grow resets the count. An ordinary email reaches its height in one or two steps: its
 * images are `data:` only and no font loads (the frame's policy), so nothing arrives late.
 */

/** Consecutive growth steps after which the frame stops following its content. */
export const MAX_FRAME_GROWTH_STEPS = 5;

/** A change this small (in CSS pixels) is ignored: rounding, not content. */
const IGNORED_CHANGE = 1;

export interface FrameHeightStep {
    /** The height to write, or `null` to leave the frame as it is. */
    readonly write: number | null;
    /** Consecutive growth steps so far, this one included. */
    readonly growthSteps: number;
    /** Stop observing: the frame keeps the height last written. */
    readonly stop: boolean;
}

/**
 * The next step, from the height last written (`0` before any), the new measurement and the growth
 * steps counted so far.
 */
export function nextFrameHeight(previous: number, measured: number, growthSteps: number): FrameHeightStep {
    if (Math.abs(measured - previous) <= IGNORED_CHANGE) {
        return { write: null, growthSteps: 0, stop: false };
    }
    if (measured < previous) {
        return { write: measured, growthSteps: 0, stop: false };
    }
    const steps = growthSteps + 1;
    return { write: measured, growthSteps: steps, stop: steps >= MAX_FRAME_GROWTH_STEPS };
}
